import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@bancolombia/design-system-web/bc-helpers';
import { BcGeneralInputComponent } from '@bancolombia/design-system-web/bc-helpers';
import { BcInputSelectV2Behavior } from '@bancolombia/design-system-behaviors';
import { UntypedFormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BcCheckboxModule } from '@bancolombia/design-system-web/bc-checkbox';
import { BcIconModule } from '@bancolombia/design-system-web/bc-icon';

/**
 * componente Input Select V2
 *
 * @export
 * @class BcInputSelectV2Component
 * @implements {OnInit}
 * @implements {OnChanges}
 */
class BcInputSelectV2Component extends BcGeneralInputComponent {
    constructor(bcHelperService) {
        super();
        this.bcHelperService = bcHelperService;
        /**
         * Permite configurar el el estado: disabled, error o default
         * @type {('error' | 'disabled' | 'default')}
         * @memberof BcInputSelectV2Component
         */
        this.status = 'default';
        /**
         * Funcion que se ejecutara cuando se seleccione una opcion
         * @type {any}
         * @memberof BcInputSelectV2Component
         */
        this.onChangeValue = new EventEmitter(true);
        /**
         * Controlador de formulario utilizado para gestionar el valor y el estado del componente.
         * Es una instancia de `UntypedFormControl` inicializada con un valor vacío.
         *
         * @type {UntypedFormControl}
         */
        this.control = new UntypedFormControl('');
        /**
         * Indica si el componente está listo para ser utilizado.
         *
         * @type {boolean}
         * @default false
         */
        this.componentReady = false;
    }
    ngOnInit() {
        this.componentId = this.componentId ?? this.bcHelperService.getId(this);
    }
    ngAfterViewInit() {
        this.initInputSelect();
        if (this.value) {
            this.setValue(this.value);
        }
    }
    ngOnChanges(changes) {
        if (changes.status) {
            this.bcInputSelectV2Behavior?.setStatus(this.status);
        }
        if (this.componentReady && !changes.status) {
            if (changes.values) {
                this.bcInputSelectV2Behavior.updateItems(this.values);
            }
            else {
                setTimeout(() => {
                    this.initInputSelect();
                    if (this.value) {
                        this.setValue(this.value);
                    }
                });
            }
        }
    }
    /**
     * Inicializa el componente de selección de entrada.
     *
     * Este método configura el comportamiento del componente `BcInputSelectV2Behavior`,
     * asigna un ID, define una función de salida, establece los elementos y realiza
     * la configuración inicial. Si hay un valor inicial, también lo despacha.
     * Finalmente, marca el componente como listo.
     *
     * @returns {void}
     */
    initInputSelect() {
        this.bcInputSelectV2Behavior = new BcInputSelectV2Behavior();
        this.bcInputSelectV2Behavior.setId(this.componentId);
        this.bcInputSelectV2Behavior.outputFunction = (param) => {
            this.dispatchChange(param.value);
            this.onChangeValue.emit(param);
        };
        this.bcInputSelectV2Behavior.setItems(this.values);
        this.bcInputSelectV2Behavior.setUp();
        if (this.initialValue) {
            this.dispatchChange(this.initialValue);
        }
        this.componentReady = true;
    }
    /**
      * Función que setea el estado del componente.
      * @param {('error' | 'disabled' | 'default')} newStatus - Nuevo estado del componente.
      * @memberof BcInputSelectV2Component
      */
    setStatus(newStatus) {
        this.bcInputSelectV2Behavior?.setStatus(newStatus);
    }
    /**
      * Cambia el item seleccionado. Si se envia un string vacio se limpia la seleccion previa y estados.
      * Si son varios valores separar con coma, por ejemplo: 1,5,6
      *
      * @memberof BcInputSelectV2Component
      */
    setValue(newValue) {
        this.bcInputSelectV2Behavior?.setSelectedValue(newValue);
        this.dispatchChange(newValue);
    }
    /**
     * Función utilizada cuando el componente es parte de un FormGroup
     * Se utiliza en disable() o enable()
     *
     * @memberof BcInputSelectV2Component
     */
    setDisabledState(disabled) {
        this.bcInputSelectV2Behavior?.setStatus(disabled ? 'disabled' : 'default');
    }
    /**
     * Función utilizada cuando el componente es parte de un FormGroup
     * Se utiliza en reset()
     * @param {string} value - El nuevo valor del componente.
     * @memberof BcInputSelectV2Component
     */
    writeValue(value) {
        this.setValue(value);
    }
    /**
     * Función utilizada cuando el componente es parte de un FormGroup
     * Se utiliza en reset() o writeValue()
     * @param {string} value - El nuevo valor del componente.
     * @memberof BcInputSelectV2Component
     */
    dispatchChange(value) {
        this.value = value;
        if (value === null || value === undefined) {
            this.bcInputSelectV2Behavior?.setSelectedValue(this.value);
        }
        else {
            this.onTouch();
            this.control.markAsTouched();
            this.control.markAsDirty();
        }
        this.onChange(this.value);
        this.control.setValue(value);
    }
    /**
     * Registra una función de callback que será llamada cuando el valor del componente cambie.
     *
     * @param fn - Función de callback que recibe el nuevo valor del componente como argumento.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registra una función que se llamará cuando el control sea tocado.
     * Este método es parte de la interfaz `ControlValueAccessor` y permite
     * que el componente informe al framework Angular cuando el usuario
     * interactúa con el control.
     *
     * @param fn - Función que se ejecutará cuando el control sea tocado.
     */
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInputSelectV2Component, deps: [{ token: i1.BcHelperService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BcInputSelectV2Component, selector: "bc-input-select-v2", inputs: { componentId: "componentId", type: "type", label: "label", iconName: "iconName", helpText: "helpText", status: "status", initialValue: "initialValue", values: "values" }, outputs: { onChangeValue: "onChangeValue" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => BcInputSelectV2Component),
                multi: true
            }
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div\n  [id]=\"componentId\"\n  [attr.type]=\"type\"\n  [attr.label]=\"label\"\n  [attr.iconName]=\"iconName\"\n  [attr.helpText]=\"helpText\"\n  [attr.status]=\"status\"\n  [attr.initialValue]=\"initialValue\"\n></div>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInputSelectV2Component, decorators: [{
            type: Component,
            args: [{ selector: 'bc-input-select-v2', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => BcInputSelectV2Component),
                            multi: true
                        }
                    ], template: "<div\n  [id]=\"componentId\"\n  [attr.type]=\"type\"\n  [attr.label]=\"label\"\n  [attr.iconName]=\"iconName\"\n  [attr.helpText]=\"helpText\"\n  [attr.status]=\"status\"\n  [attr.initialValue]=\"initialValue\"\n></div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.BcHelperService }]; }, propDecorators: { componentId: [{
                type: Input
            }], type: [{
                type: Input
            }], label: [{
                type: Input
            }], iconName: [{
                type: Input
            }], helpText: [{
                type: Input
            }], status: [{
                type: Input
            }], initialValue: [{
                type: Input
            }], values: [{
                type: Input
            }], onChangeValue: [{
                type: Output
            }] } });

/**
 * Importa el componente BcInputSelectV2 con
 * los diseños de Bancolombia S.A.
 *
 * @componente <bc-input-select-v2></bc-input-select-v2>
 *
 * @param  [iconName](opcional): Texto que indica el id del componente
 * @param  [label](opcional): Texto que indica el placeholder del select
 * @param  [helpText](opcional): Texto que aparece en la parte de abajo del select
 * @param  values: Array tipo [{ label: string, value: string, icon: string }] que indica los valores del select
 * @param  onChange: Output Valor de salida que indica los valores seleccionados
 *
 * @example
 *
 * <bc-input-select-v2
 * [id] = "'bc-select1'"
 * [values]="objSelect.values"
 * [label]="objSelect.placeholder"
 * [helpText]="objSelect.textBelow"
 * (onChange) = "showSelectedValue($event)"
 * ></bc-input-select-v2>
 *
 * @author: Bancolombia S.A
 */
class BcInputSelectV2Module {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInputSelectV2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: BcInputSelectV2Module, declarations: [BcInputSelectV2Component], imports: [CommonModule, BcCheckboxModule, BcIconModule, ReactiveFormsModule, FormsModule], exports: [BcInputSelectV2Component] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInputSelectV2Module, imports: [CommonModule, BcCheckboxModule, BcIconModule, ReactiveFormsModule, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcInputSelectV2Module, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BcInputSelectV2Component],
                    imports: [CommonModule, BcCheckboxModule, BcIconModule, ReactiveFormsModule, FormsModule],
                    exports: [BcInputSelectV2Component],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BcInputSelectV2Component, BcInputSelectV2Module };
